import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SeoJsonLd = () => {
  const location = useLocation();
  const siteUrl = 'https://dunnaway-excavating.com';

  useEffect(() => {
    // Remove existing JSON-LD scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${siteUrl}/#business`,
      "name": "Dunnaway Excavating",
      "url": siteUrl,
      "logo": "https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/b1f31de5a825c507107eb6babb17dc92.png",
      "image": "https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/40ffc658b1b9500387d803035aa9abaa.jpeg",
      "description": "Family-owned excavating company serving the Lake of the Ozarks region since 1966. Expert excavation, demolition, land clearing, site preparation, and utility trenching services.",
      "telephone": "+15733745867",
      "email": "dunnaway.excavating@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Gravois Mills",
        "addressRegion": "MO",
        "postalCode": "65037",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "38.1234",
        "longitude": "-92.6177"
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "Laurie"
        },
        {
          "@type": "City",
          "name": "Gravois Mills"
        },
        {
          "@type": "City",
          "name": "Sunrise Beach"
        },
        {
          "@type": "City",
          "name": "Osage Beach"
        },
        {
          "@type": "City",
          "name": "Lake Ozark"
        },
        {
          "@type": "City",
          "name": "Camdenton"
        },
        {
          "@type": "City",
          "name": "Versailles"
        },
        {
          "@type": "City",
          "name": "Eldon"
        }
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "20:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "20:00"
        }
      ],
      "priceRange": "$$",
      "foundingDate": "1966",
      "sameAs": [
        "https://www.facebook.com/DunnawayExcavating"
      ]
    };

    // Website Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "url": siteUrl,
      "name": "Dunnaway Excavating",
      "description": "Family-owned excavating company serving the Lake of the Ozarks region since 1966",
      "publisher": {
        "@id": `${siteUrl}/#business`
      }
    };

    // Service Schema
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Excavation and Demolition Services",
      "provider": {
        "@id": `${siteUrl}/#business`
      },
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "38.1234",
          "longitude": "-92.6177"
        },
        "geoRadius": "50000"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Excavation and Demolition Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Land Clearing & Site Preparation",
              "description": "Complete land clearing and site preparation including brush removal, tree and stump removal, demolition, and grading for new construction or property improvements."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Basement & Foundation Excavation",
              "description": "Professional basement and foundation excavation with proper depth, grading, compaction, and drainage management."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Utility Trenching & Installation",
              "description": "Precision trenching for water lines, sewer lines, and electrical conduit."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Drainage Solutions & Erosion Control",
              "description": "Custom drainage systems to prevent erosion and water damage including curtain drains, French drains, swales, and downspout drainage."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Private Road & Driveway Construction",
              "description": "Construction and grading of gravel driveways and long private roads with road base installation and drainage control."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pond Building & Rock Removal",
              "description": "Full-service excavation for ponds, lake-edge shaping, water-feature development, and rock breaking services."
            }
          }
        ]
      }
    };

    // Breadcrumb Schema (for homepage)
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": siteUrl
        }
      ]
    };

    // Add all schemas to the page
    const schemas = [organizationSchema, websiteSchema, serviceSchema, breadcrumbSchema];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [location.pathname, siteUrl]);

  return null;
};

export default SeoJsonLd;
